/**
 * GLYCOGEN RESOLVER (MEV5)
 * Mechanism: Muscle Glycogen Debt & Energy Vacuum
 * Standards: Cunningham (1980), Metabolic Flux (2024)
 */

import { V5PhysiologicalState as PhysiologicalState } from '../types';

export const GlycogenResolver = {
    
    /**
     * Calculates the change in glycogen debt based on workout intensity and glucose uptake.
     * @param state Current physiological state
     * @param glucoseCleared Grams of glucose cleared from blood plasma this minute
     * @param profile User profile containing weight and body fat
     * @returns The change in debt (Grams)
     */
    computeDelta(state: PhysiologicalState, glucoseCleared: number, profile: any): number {
        const weight = profile?.baseline_weight_kg || 85;
        const bf = profile?.body_fat_percentage || 15;
        const lbm = weight * (1 - (bf / 100));

        // 1. DEPLETION (Expenditure)
        // High Intensity (1.0) = ~0.02g glucose burn per kg of LBM per minute
        const burnRate = state.intensity * (lbm * 0.02);

        // 2. REPLETION (Filling the vacuum)
        // Systemic clearance only contributes to muscle glycogen if insulin is elevated (Anabolic State).
        // Otherwise, basal clearance is just feeding the brain/organs, not the muscles.
        let uptakeRate = 0;
        if (state.insulin > 10.0) {
            // Deduct the basal "brain drip" (~0.1g/m) so only surplus dietary carbs fill the sink
            uptakeRate = Math.max(0, glucoseCleared - 0.1);
        }

        // Delta = Burn - Fill
        return burnRate - uptakeRate;
    },

    /**
     * Computes the maximum storage capacity based on Lean Body Mass (LBM).
     * @param profile User profile containing weight and body fat
     */
    getMaxCapacity(profile: any): number {
        const weight = profile?.baseline_weight_kg || 85;
        const bf = profile?.body_fat_percentage || 15;
        const lbm = weight * (1 - (bf / 100));
        
        // Standard athlete storage: ~15g per kg of LBM
        return lbm * 15;
    }
};
